// Follow this naming pattern: https://posthog.com/product-engineers/5-ways-to-improve-analytics-data

import {type PaymentMethod} from 'models/PaymentMethod'
import {type ColumnPresetOption} from 'new/export/lib/types/ColumnPresetOption'
import {type ExportFormat} from 'new/export/lib/types/ExportFormat'
import {type SurveyAnswer} from 'new/services/survey/SurveyService'
import {type EmbedIframeConfigurationOptions} from 'new/stores/EmbedIframeStore'
import {type PaylinkSharePreviewConfigurationOptions} from 'new/stores/PaylinkSharePreviewStore'
import {
  type EmailSubscriptionEventFilter,
  type EmailSubscriptionEventName,
  type EmailSubscriptionEventStatus,
} from './EventSubscriptionsService'
import {type ApprovalStatus} from './Organisation'
import {type PaymentProviderName} from './PaymentProvider'
import {type ExportPreset} from './preferences/exports/UserPreferencesExportsSet'
import {type EnableProgressBarOptions} from './ProgressBar'
import {type Solution, type SolutionType} from './Solution'

export enum AnalyticsEventName {
  EXPORT_BUTTON_CLICK = 'export:button_click',
  EXPORT_SUCCESSFUL = 'export:successful',
  EXPORT_ERROR = 'export:error',
  EXPORT_EXCEEDS_LIMIT = 'export:exceeds_limit',
  EXPORT_MODAL_OPEN = 'export:modal_open',
  EXPORT_MODAL_CANCEL = 'export:modal_cancel',
  EXPORT_PRESET_MODAL_OPEN = 'export:preset_modal_open',
  EXPORT_PRESET_MODAL_CANCEL = 'export:preset_modal_cancel',
  EXPORT_PRESET_MODAL_CREATE_PRESET = 'export:preset_modal_create_preset',
  EXPORT_PRESET_MODAL_RENAME_PRESET = 'export:preset_modal_rename_preset',
  EXPORT_PRESET_MODAL_DELETE_PRESET = 'export:preset_modal_delete_preset',
  EXPORT_PRESET_MODAL_MANAGE_PRESETS = 'export:preset_modal_manage_presets',
  EXPORT_PRESET_MODAL_SAVE_PRESET = 'export:preset_modal_save_preset',
  EXPORT_PRESET_MODAL_SWAP_PRESETS = 'export:preset_modal_swap_presets',

  LETTERSHOP_VOUCHER_APPLIED = 'lettershop:voucher_applied',
  LETTERSHOP_ORDER_SUCCESSFUL = 'lettershop:order_successful',

  SOLUTION_CREATE_NEW_BUTTON_CLICK = 'solution:create_new_button_click',
  SOLUTION_TYPE_SELECT_CLICK = 'solution:type_select_click',
  SOLUTION_PAYLINK_COPY_BUTTON_CLICK = 'solution:paylink_copy_button_click',
  SOLUTION_CREATED = 'solution:created',
  SOLUTION_UPDATED = 'solution:edited',
  SOLUTION_TWINT_TERMS_ACCEPTED = 'solution:twint_terms_accepted',
  SOLUTION_PAYMENT_PROVIDER_MODAL_CLICK = 'solution:connect_payment_provider_modal_click',
  SOLUTION_UNSAVED_CHANGES_SAVED = 'solution:unsaved_changes_saved',

  SOLUTION_PAYMENT_METHODS_EDIT_BUTTON_CLICK = 'solution:payment_methods_edit_button_click',
  SOLUTION_PAYMENT_METHODS_CHANGED = 'solution:payment_methods_changed',
  SOLUTION_PAYMENT_METHODS_MODAL_DISMISSED = 'solution:payment_methods_modal_dismissed',

  SOLUTION_PROGRESS_BAR_ENABLED = 'solution:progress_bar_enabled',
  SOLUTION_PROGRESS_BAR_DISABLED = 'solution:progress_bar_disabled',

  SOLUTION_SETTINGS_PROGRESS_BAR_SHOW = 'solution_settings:progress_bar_show',
  SOLUTION_SETTINGS_PROGRESS_BAR_HIDE = 'solution_settings:progress_bar_hide',

  SOLUTION_PROMOTE_TWINT_BUTTON_OPEN = 'solution_promote:twint_button_open',
  SOLUTION_PROMOTE_TWINT_WEBSITE_BUTTON_COPY = 'solution_promote:twint_website_button_copy',
  SOLUTION_PROMOTE_TWINT_EMAIL_BUTTON_COPY = 'solution_promote:twint_email_button_copy',

  SOLUTION_PROMOTE_GENERIC_PAYLINK_BUTTON_OPEN = 'solution_promote:generic_paylink_button_open',
  SOLUTION_PROMOTE_GENERIC_PAYLINK_WEBSITE_BUTTON_COPY = 'solution_promote:generic_paylink_website_button_copy',
  SOLUTION_PROMOTE_GENERIC_PAYLINK_EMAIL_BUTTON_COPY = 'solution_promote:generic_paylink_email_button_copy',

  SOLUTION_PROMOTE_IFRAME_EMBED_BUTTON_OPEN = 'solution_promote:iframe_embed_button_open',
  SOLUTION_PROMOTE_IFRAME_EMBED_SETTINGS_MODAL_OPEN = 'solution_promote:iframe_embed_settings_modal_open',
  SOLUTION_PROMOTE_IFRAME_EMBED_SETTINGS_MODAL_SAVE = 'solution_promote:iframe_embed_settings_modal_save',
  SOLUTION_PROMOTE_IFRAME_EMBED_SETTINGS_MODAL_CANCEL = 'solution_promote:iframe_embed_settings_modal_cancel',
  SOLUTION_PROMOTE_IFRAME_EMBED_WEBSITE_BUTTON_COPY = 'solution_promote:iframe_embed_website_button_copy',

  SOLTUION_PROMOTE_SCHEME_PARAMETER_1_COPY = 'solution_promote:scheme_parameter_1_copy',
  SOLTUION_PROMOTE_SCHEME_PARAMETER_2_COPY = 'solution_promote:scheme_parameter_2_copy',
  SOLUTION_PROMOTE_PAYLINK_CONFIGURE_BUTTON_OPEN = 'solution_promote:paylink_configure_button_open',
  SOLUTION_PROMOTE_PAYLINK_CONFIGURE_MODAL_OPEN = 'solution_promote:paylink_configure_modal_open',
  SOLUTION_PROMOTE_PAYLINK_CONFIGURE_MODAL_SAVE = 'solution_promote:paylink_configure_modal_save',
  SOLUTION_PROMOTE_PAYLINK_CONFIGURE_MODAL_CANCEL = 'solution_promote:paylink_configure_modal_cancel',
  SOLUTION_PROMOTE_PAYLINK_COPY_BUTTON_CLICK = 'solution_promote:paylink_copy_button_click',

  SOLUTION_PROMOTE_TERMINAL_LINK_COPY_BUTTON_CLICK = 'solution_promote:terminal_link_copy_button_click',

  SOLUTION_PROMOTE_DOWNLOAD_TWINT_QR_BUTTON_CLICK = 'solution_promote:download_twint_qr_button_click',
  SOLUTION_PROMOTE_DOWNLOAD_TWINT_QR_SUCCESS = 'solution_promote:download_twint_qr_success',
  SOLUTION_PROMOTE_DOWNLOAD_TWINT_QR_ERROR = 'solution_promote:download_twint_qr_error',
  SOLUTION_PROMOTE_DOWNLOAD_UNIVERSAL_QR_BUTTON_CLICK = 'solution_promote:download_universal_qr_button_click',
  SOLUTION_PROMOTE_DOWNLOAD_UNIVERSAL_QR_SUCCESS = 'solution_promote:download_universal_qr_success',
  SOLUTION_PROMOTE_DOWNLOAD_UNIVERSAL_QR_ERROR = 'solution_promote:download_universal_qr_error',

  SOLUTION_PROMOTE_DOWNLOAD_TWINT_ACCEPTANCE_SIGNAGE_BUTTON_CLICK = 'solution_promote:download_twint_acceptance_signage_button_click',
  SOLUTION_PROMOTE_DOWNLOAD_TWINT_ACCEPTANCE_SIGNAGE_SUCCESS = 'solution_promote:download_twint_acceptance_signage_success',
  SOLUTION_PROMOTE_DOWNLOAD_TWINT_ACCEPTANCE_SIGNAGE_ERROR = 'solution_promote:download_twint_acceptance_signage_error',

  SIGN_UP_ORGANISATION_CONTINUE_BUTTON_CLICK = 'sign_up:organisation_continue_button_click',
  SIGN_UP_FORM_SUBMIT = 'sign_up:form_submit',

  TAX_RECEIPT_SETTINGS_CUSTOM_DONATION_RECEIPT_SAVED = 'tax_receipt_settings:custom_donation_receipt:saved',
  TAX_RECEIPT_SETTINGS_CUSTOM_DONATION_RECEIPT_SAVE_ERROR = 'tax_receipt_settings:custom_donation_receipt:save_error',
  TAX_RECEIPT_SETTINGS_CUSTOM_DONATION_RECEIPT_OPEN = 'tax_receipt_settings:custom_donation_receipt:open',
  TAX_RECEIPT_SETTINGS_CUSTOM_DONATION_RECEIPT_PREVIEW = 'tax_receipt_settings:custom_donation_receipt:preview',
  TAX_RECEIPT_SETTINGS_CUSTOM_DONATION_RECEIPT_DOWNLOAD = 'tax_receipt_settings:custom_donation_receipt:download',
  TAX_RECEIPT_SETTINGS_CUSTOM_DONATION_RECEIPT_DELETE = 'tax_receipt_settings:custom_donation_receipt:delete',
  TAX_RECEIPT_SETTINGS_CUSTOM_DONATION_RECEIPT_UPLOAD = 'tax_receipt_settings:custom_donation_receipt:upload',
  TAX_RECEIPT_SETTINGS_CUSTOM_DONATION_RECEIPT_UPLOAD_ERROR = 'tax_receipt_settings:custom_donation_receipt:upload_error',
  TAX_RECEIPT_SETTINGS_CUSTOM_DONATION_RECEIPT_UPLOAD_VALIDATION_ERROR = 'tax_receipt_settings:custom_donation_receipt:upload_validation_error',

  TRANSACTION_REFUND_SUCCESSFUL = 'transaction:refund_successful',
  TRANSACTION_REFUND_ERROR = 'transaction:refund_error',

  PAYMENT_AGREEMENT_VOID_SUCCESSFUL = 'payment_agreement:void_successful',
  PAYMENT_AGREEMENT_VOID_ERROR = 'payment_agreement:void_error',

  ONBOARDING_FILL_OUT_ORGANISATION_FORM_BUTTON_CLICK = 'onboarding:fill_out_organisation_form_button_click',
  ONBOARDING_CREATE_SOLUTION_BUTTON_CLICK = 'onboarding:create_solution_button_click',

  PARTNER_INTEGRATION_DISCONNECTED = 'partner_integration:disconnected',
  PARTNER_INTEGRATION_DISCONNECT_ERROR = 'partner_integration:disconnect_error',

  PAYMENT_PROVIDER_CONNECT = 'payment_provider:connect',
  PAYMENT_PROVIDER_CONNECTED = 'payment_provider:connected',
  PAYMENT_PROVIDER_CONNECT_ERROR = 'payment_provider:connect_error',
  PAYMENT_PROVIDER_DISCONNECTED = 'payment_provider:disconnected',
  PAYMENT_PROVIDER_DISCONNECT_ERROR = 'payment_provider:disconnect_error',
  PAYMENT_PROVIDER_REFRESHED = 'payment_provider:refreshed',
  PAYMENT_PROVIDER_REFRESH_ERROR = 'payment_provider:refresh_error',

  ORGANISATION_SETTINGS_DOCUMENT_UPLOAD = 'organisation_settings:document_upload',
  ORGANISATION_SETTINGS_REQUEST_APPROVAL = 'organisation_settings:request_approval',
  ORGANISATION_SETTINGS_SAVE = 'organisation_settings:save',

  COMMUNICATION_SETTINGS_EMAIL_SERVICE_TOGGLE_MANUAL = 'communication_settings:email_service_toggle_manual',
  COMMUNICATION_SETTINGS_EMAIL_SERVICE_TOGGLE_DEFAULT = 'communication_settings:email_service_toggle_default',
  COMMUNICATION_SETTINGS_EMAIL_SERVICE_TOGGLE = 'communication_settings:email_service_toggle',
  COMMUNICATION_SETTINGS_EMAIL_SERVICE_TOGGLE_ERROR = 'communication_settings:email_service_toggle_error',

  WELCOME_MODAL_CLOSE_BUTTON_CLICK = 'welcome_modal:close_button_click',

  WELCOME_DASHBOARD_CLOSE_BUTTON_CLICK = 'welcome_dashboard:close_button_click',

  THEME_TOGGLE = 'theme:toggle',

  TABLE_PAGE_SIZE_CHANGED = 'table:page_size_changed',

  // Special posthog events
  PAGEVIEW = '$pageview',

  SURVEY_SHOWN = 'survey shown',
  SURVEY_DISMISSED = 'survey dismissed',
  SURVEY_SENT = 'survey sent',
}

export type AnalyticsEventProperties = {
  [AnalyticsEventName.PAGEVIEW]: [
    {
      $pathname: string
    },
  ]
  [AnalyticsEventName.TAX_RECEIPT_SETTINGS_CUSTOM_DONATION_RECEIPT_SAVED]: [
    {
      fallback_language: string
      languages: string[]
    },
  ]
  [AnalyticsEventName.TAX_RECEIPT_SETTINGS_CUSTOM_DONATION_RECEIPT_UPLOAD]: [
    {
      language: string
    },
  ]
  [AnalyticsEventName.TAX_RECEIPT_SETTINGS_CUSTOM_DONATION_RECEIPT_UPLOAD_ERROR]: [
    {
      language: string
    },
  ]
  [AnalyticsEventName.TAX_RECEIPT_SETTINGS_CUSTOM_DONATION_RECEIPT_UPLOAD_VALIDATION_ERROR]: [
    {
      language: string
    },
  ]
  [AnalyticsEventName.EXPORT_BUTTON_CLICK]: [
    {
      export_format: ExportFormat
      export_origin?: string
    },
  ]
  [AnalyticsEventName.EXPORT_SUCCESSFUL]: [
    {
      export_format: ExportFormat
      export_origin: string
      export_include_custom_fields: boolean | undefined
      export_row_count: number
      export_column_preset_option: ColumnPresetOption
    },
  ]
  [AnalyticsEventName.EXPORT_EXCEEDS_LIMIT]: [
    {
      export_origin?: string
      export_row_count: number
    },
  ]
  [AnalyticsEventName.EXPORT_MODAL_OPEN]: [
    {
      export_origin: string
    },
  ]
  [AnalyticsEventName.EXPORT_MODAL_CANCEL]: [
    {
      export_origin: string
    },
  ]
  [AnalyticsEventName.EXPORT_PRESET_MODAL_OPEN]: [
    {
      export_origin: string
    },
  ]
  [AnalyticsEventName.EXPORT_PRESET_MODAL_CANCEL]: [
    {
      export_origin: string
    },
  ]

  [AnalyticsEventName.EXPORT_PRESET_MODAL_CREATE_PRESET]: [
    {
      export_origin: string
    },
  ]
  [AnalyticsEventName.EXPORT_PRESET_MODAL_RENAME_PRESET]: [
    {
      export_origin: string
      export_preset_name: string
    },
  ]
  [AnalyticsEventName.EXPORT_PRESET_MODAL_DELETE_PRESET]: [
    {
      export_origin: string
    },
  ]
  [AnalyticsEventName.EXPORT_PRESET_MODAL_MANAGE_PRESETS]: [
    {
      export_origin: string
    },
  ]
  [AnalyticsEventName.EXPORT_PRESET_MODAL_SAVE_PRESET]: [
    {
      export_origin: string
      export_presets: ExportPreset[]
    },
  ]
  [AnalyticsEventName.EXPORT_PRESET_MODAL_SWAP_PRESETS]: [
    {
      export_origin: string
    },
  ]
  [AnalyticsEventName.SOLUTION_TYPE_SELECT_CLICK]: [
    {
      solution_type: SolutionType
    },
  ]
  [AnalyticsEventName.SOLUTION_CREATED]: [
    Solution & {
      solution_config?: any
    },
  ]
  [AnalyticsEventName.SOLUTION_UPDATED]: [
    Solution & {
      solution_config?: any
    },
  ]
  [AnalyticsEventName.SOLUTION_PAYLINK_COPY_BUTTON_CLICK]: [
    {
      solution_type: SolutionType | undefined
      solution_uuid: string
    },
  ]
  [AnalyticsEventName.SOLUTION_PROMOTE_PAYLINK_CONFIGURE_BUTTON_OPEN]: [
    {
      solution_type: SolutionType | undefined
      solution_uuid: string
    },
  ]
  [AnalyticsEventName.SOLUTION_PROMOTE_PAYLINK_CONFIGURE_MODAL_SAVE]: [
    {
      options: PaylinkSharePreviewConfigurationOptions
    },
  ]
  [AnalyticsEventName.SOLUTION_PROMOTE_PAYLINK_COPY_BUTTON_CLICK]: [
    {
      solution_type: SolutionType | undefined
      solution_uuid: string
    },
  ]
  [AnalyticsEventName.SOLUTION_PROMOTE_TERMINAL_LINK_COPY_BUTTON_CLICK]: [
    {
      solution_type: SolutionType | undefined
      solution_uuid: string
    },
  ]

  [AnalyticsEventName.SOLUTION_PROMOTE_TWINT_BUTTON_OPEN]: [
    {
      solution_type: SolutionType | undefined
      solution_uuid: string
    },
  ]
  [AnalyticsEventName.SOLUTION_PROMOTE_TWINT_EMAIL_BUTTON_COPY]: [
    {
      solution_type: SolutionType | undefined
      solution_uuid: string
    },
  ]
  [AnalyticsEventName.SOLUTION_PROMOTE_TWINT_WEBSITE_BUTTON_COPY]: [
    {
      solution_type: SolutionType | undefined
      solution_uuid: string
    },
  ]

  [AnalyticsEventName.SOLUTION_PROMOTE_GENERIC_PAYLINK_BUTTON_OPEN]: [
    {
      solution_type: SolutionType | undefined
      solution_uuid: string
    },
  ]
  [AnalyticsEventName.SOLUTION_PROMOTE_GENERIC_PAYLINK_WEBSITE_BUTTON_COPY]: [
    {
      solution_type: SolutionType | undefined
      solution_uuid: string
    },
  ]
  [AnalyticsEventName.SOLUTION_PROMOTE_GENERIC_PAYLINK_EMAIL_BUTTON_COPY]: [
    {
      solution_type: SolutionType | undefined
      solution_uuid: string
    },
  ]

  [AnalyticsEventName.SOLUTION_PROMOTE_IFRAME_EMBED_BUTTON_OPEN]: [
    {
      solution_type: SolutionType | undefined
      solution_uuid: string
    },
  ]
  [AnalyticsEventName.SOLUTION_PROMOTE_IFRAME_EMBED_WEBSITE_BUTTON_COPY]: [
    {
      solution_type: SolutionType | undefined
      solution_uuid: string
    },
  ]

  [AnalyticsEventName.SOLUTION_PROMOTE_DOWNLOAD_TWINT_QR_BUTTON_CLICK]: [
    {
      solution_type: SolutionType | undefined
      solution_uuid: string
      high_res: boolean
    },
  ]
  [AnalyticsEventName.SOLUTION_PROMOTE_DOWNLOAD_TWINT_QR_SUCCESS]: [
    {
      solution_type: SolutionType | undefined
      solution_uuid: string
      high_res: boolean
    },
  ]
  [AnalyticsEventName.SOLUTION_PROMOTE_DOWNLOAD_TWINT_QR_ERROR]: [
    {
      solution_type: SolutionType | undefined
      solution_uuid: string
      high_res: boolean
    },
  ]
  [AnalyticsEventName.SOLUTION_PROMOTE_DOWNLOAD_UNIVERSAL_QR_BUTTON_CLICK]: [
    {
      solution_type: SolutionType | undefined
      solution_uuid: string
    },
  ]
  [AnalyticsEventName.SOLUTION_PROMOTE_DOWNLOAD_UNIVERSAL_QR_SUCCESS]: [
    {
      solution_type: SolutionType | undefined
      solution_uuid: string
    },
  ]
  [AnalyticsEventName.SOLUTION_PROMOTE_DOWNLOAD_UNIVERSAL_QR_ERROR]: [
    {
      solution_type: SolutionType | undefined
      solution_uuid: string
    },
  ]
  [AnalyticsEventName.SOLUTION_PROGRESS_BAR_ENABLED]: [
    {
      solution_uuid: string
      payload: EnableProgressBarOptions
    },
  ]
  [AnalyticsEventName.SOLUTION_PROGRESS_BAR_DISABLED]: [
    {
      solution_uuid: string
    },
  ]
  [AnalyticsEventName.SOLUTION_PROMOTE_DOWNLOAD_TWINT_ACCEPTANCE_SIGNAGE_BUTTON_CLICK]: [
    {
      solution_type: SolutionType | undefined
      solution_uuid: string
    },
  ]
  [AnalyticsEventName.SOLUTION_PROMOTE_DOWNLOAD_TWINT_ACCEPTANCE_SIGNAGE_SUCCESS]: [
    {
      solution_type: SolutionType | undefined
      solution_uuid: string
    },
  ]
  [AnalyticsEventName.SOLUTION_PROMOTE_DOWNLOAD_TWINT_ACCEPTANCE_SIGNAGE_ERROR]: [
    {
      solution_type: SolutionType | undefined
      solution_uuid: string
    },
  ]
  [AnalyticsEventName.SOLUTION_PROMOTE_IFRAME_EMBED_SETTINGS_MODAL_SAVE]: [
    {
      options: EmbedIframeConfigurationOptions
      paylink_short_id: string | undefined
    },
  ]
  [AnalyticsEventName.SOLUTION_TWINT_TERMS_ACCEPTED]: [
    {
      solution_type: SolutionType | undefined
      solution_uuid: string | undefined
    },
  ]
  [AnalyticsEventName.SOLUTION_PAYMENT_PROVIDER_MODAL_CLICK]: [
    {
      solution_type: SolutionType | undefined
      solution_uuid: string | undefined
    },
  ]
  [AnalyticsEventName.SOLUTION_UNSAVED_CHANGES_SAVED]: [
    {
      solution_type: SolutionType | undefined
      solution_uuid: string | undefined
    },
  ]
  [AnalyticsEventName.SOLUTION_PAYMENT_METHODS_EDIT_BUTTON_CLICK]: [
    {
      solution_type: SolutionType | undefined
      solution_uuid: string | undefined
    },
  ]
  [AnalyticsEventName.SOLUTION_PAYMENT_METHODS_CHANGED]: [
    {
      solution_type: SolutionType | undefined
      solution_uuid: string | undefined
      payment_methods: PaymentMethod[]
    },
  ]
  [AnalyticsEventName.SOLUTION_PAYMENT_METHODS_MODAL_DISMISSED]: [
    {
      solution_type: SolutionType | undefined
      solution_uuid: string | undefined
    },
  ]
  [AnalyticsEventName.TRANSACTION_REFUND_SUCCESSFUL]: [
    {
      payment_uuid: string
    },
  ]
  [AnalyticsEventName.TRANSACTION_REFUND_ERROR]: [
    {
      payment_uuid: string
    },
  ]

  [AnalyticsEventName.PAYMENT_AGREEMENT_VOID_SUCCESSFUL]: [
    {
      payment_agreement_uuid: string
    },
  ]
  [AnalyticsEventName.PAYMENT_AGREEMENT_VOID_ERROR]: [
    {
      payment_agreement_uuid: string
    },
  ]

  [AnalyticsEventName.ONBOARDING_CREATE_SOLUTION_BUTTON_CLICK]: [
    {
      status: string
    },
  ]

  [AnalyticsEventName.ORGANISATION_SETTINGS_REQUEST_APPROVAL]: [
    {
      approval_status: ApprovalStatus
    },
  ]
  [AnalyticsEventName.ORGANISATION_SETTINGS_SAVE]: [
    {
      approval_status: ApprovalStatus
    },
  ]

  [AnalyticsEventName.COMMUNICATION_SETTINGS_EMAIL_SERVICE_TOGGLE]: [
    {
      event_name: EmailSubscriptionEventName
      status: EmailSubscriptionEventStatus
      filter?: EmailSubscriptionEventFilter
    },
  ]
  [AnalyticsEventName.COMMUNICATION_SETTINGS_EMAIL_SERVICE_TOGGLE_ERROR]: [
    {
      event_name: EmailSubscriptionEventName
      status: EmailSubscriptionEventStatus
      filter?: EmailSubscriptionEventFilter
    },
  ]

  [AnalyticsEventName.PARTNER_INTEGRATION_DISCONNECTED]: [
    {
      partner_integration: string
    },
  ]
  [AnalyticsEventName.PARTNER_INTEGRATION_DISCONNECT_ERROR]: [
    {
      partner_integration: string
    },
  ]

  [AnalyticsEventName.PAYMENT_PROVIDER_CONNECT]: [
    {
      payment_provider: PaymentProviderName
    },
  ]
  [AnalyticsEventName.PAYMENT_PROVIDER_CONNECTED]: [
    {
      payment_provider: PaymentProviderName
    },
  ]
  [AnalyticsEventName.PAYMENT_PROVIDER_CONNECT_ERROR]: [
    {
      payment_provider: PaymentProviderName
    },
  ]
  [AnalyticsEventName.PAYMENT_PROVIDER_REFRESHED]: [
    {
      payment_provider: PaymentProviderName
    },
  ]
  [AnalyticsEventName.PAYMENT_PROVIDER_REFRESH_ERROR]: [
    {
      payment_provider: PaymentProviderName
    },
  ]
  [AnalyticsEventName.PAYMENT_PROVIDER_DISCONNECTED]: [
    {
      payment_provider: PaymentProviderName
    },
  ]
  [AnalyticsEventName.PAYMENT_PROVIDER_DISCONNECT_ERROR]: [
    {
      payment_provider: PaymentProviderName
    },
  ]
  [AnalyticsEventName.THEME_TOGGLE]: [
    {
      theme: string
    },
  ]
  [AnalyticsEventName.TABLE_PAGE_SIZE_CHANGED]: [
    {
      page_size: number
      origin: string | undefined
    },
  ]
  [AnalyticsEventName.SURVEY_SHOWN]: [
    {
      $survey_id: string
    },
  ]
  [AnalyticsEventName.SURVEY_DISMISSED]: [
    {
      $survey_id: string
    },
  ]
  [AnalyticsEventName.SURVEY_SENT]: [
    {
      $survey_id: string
      $survey_response?: SurveyAnswer
    } & Record<`$survey_response_${number}`, SurveyAnswer>,
  ]
}

// Inspired from https://stackoverflow.com/questions/50966229/how-can-i-conditionally-choose-types-depending-on-whether-a-property-exists-in-a
// This makes sure that if you add a new event to `AnalyticsEvent` that per default
// it will not have any properties. You have to add the properties to `AnalyticsEventProperties`
type MapAnalyticsEventsWithProperties<
  T extends string,
  Mapping extends {[key in T]?: any},
> = {
  [key in T]: Mapping extends {[P in key]: infer U} ? U : []
}

export type AnalyticsEventArgs = MapAnalyticsEventsWithProperties<
  AnalyticsEventName,
  AnalyticsEventProperties
>
